import { useMemo, useState } from 'react';
import { LuPlus, LuSearch, LuSparkle, LuX } from 'react-icons/lu';

import { AssetInfo } from '@/cip/_openapi/portfolio-optimizer';
import { TickerSelectorInput } from '@/components/TickerSelectorInput';
import { Badge } from '@/components/ui/badge';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatCurrency } from 'utils/number-formatter';

import { effectiveReplacements, TLHHolding, TLHHoldingCategory, TLHReplacement } from '../api/tlh-types';
import { OnboardingStepProps } from '../types';

type TabKey = 'all' | 'etf' | 'stock' | 'bond';

const TABS: { key: TabKey; label: string; category?: TLHHoldingCategory }[] = [
    { key: 'etf', label: 'ETFs', category: TLHHoldingCategory.ETF },
    { key: 'stock', label: 'Stocks', category: TLHHoldingCategory.STOCK },
    { key: 'bond', label: 'Bonds', category: TLHHoldingCategory.BOND },
    { key: 'all', label: 'All' },
];

export const TaxLossHarvestingStep = ({ tlhHoldings, isTlhHoldingsLoading, state, setState }: OnboardingStepProps) => {
    const [activeTab, setActiveTab] = useState<TabKey>('etf');
    const [search, setSearch] = useState('');

    const tlhState = state.taxLossHarvesting;

    const counts = useMemo(
        () => ({
            all: tlhHoldings.length,
            etf: tlhHoldings.filter((h) => h.category === TLHHoldingCategory.ETF).length,
            stock: tlhHoldings.filter((h) => h.category === TLHHoldingCategory.STOCK).length,
            bond: tlhHoldings.filter((h) => h.category === TLHHoldingCategory.BOND).length,
        }),
        [tlhHoldings],
    );

    const visibleHoldings = useMemo(() => {
        const tab = TABS.find((t) => t.key === activeTab);
        const term = search.trim().toLowerCase();
        return tlhHoldings.filter((h) => {
            if (tab?.category && h.category !== tab.category) return false;
            if (!term) return true;
            return h.ticker.toLowerCase().includes(term) || h.name.toLowerCase().includes(term);
        });
    }, [tlhHoldings, activeTab, search]);

    const getEffectiveReplacements = (holding: TLHHolding): TLHReplacement[] =>
        effectiveReplacements(holding, tlhState.replacementsByHolding);

    const isUntouched = (holding: TLHHolding) =>
        tlhState.replacementsByHolding[holding.sId] === undefined && holding.suggestedReplacements.length > 0;

    const setReplacements = (holdingSId: string, replacements: TLHReplacement[]) =>
        setState((prev) => ({
            ...prev,
            taxLossHarvesting: {
                ...prev.taxLossHarvesting,
                replacementsByHolding: { ...prev.taxLossHarvesting.replacementsByHolding, [holdingSId]: replacements },
            },
        }));

    const removeReplacement = (holding: TLHHolding, replacementSId: string) =>
        setReplacements(
            holding.sId,
            getEffectiveReplacements(holding).filter((r) => r.sId !== replacementSId),
        );

    const addReplacement = (holding: TLHHolding, replacement: TLHReplacement) => {
        const current = getEffectiveReplacements(holding);
        if (current.some((r) => r.sId === replacement.sId)) return;
        setReplacements(holding.sId, [...current, replacement]);
    };

    const clearReplacements = (holdingSId: string) => setReplacements(holdingSId, []);

    return (
        <div className="flex flex-col gap-6">
            <div>
                <h3 className="text-cocoa-1000 text-2xl font-medium">Tax Loss Harvesting</h3>
                <p className="text-cocoa-600 mt-1 text-sm">
                    Pick replacement assets for holdings outside the model. Suggestions are drawn from your in-house
                    strategy library.
                </p>
            </div>

            <div className="flex items-center justify-between gap-4">
                <div className="border-cocoa-200 flex border-b">
                    {TABS.map((tab) => {
                        const active = activeTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={() => setActiveTab(tab.key)}
                                className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2 text-sm transition ${
                                    active
                                        ? 'border-cocoa-1000 text-cocoa-1000 font-medium'
                                        : 'text-cocoa-600 hover:text-cocoa-1000 border-transparent'
                                }`}
                            >
                                {tab.label}
                                <Badge variant="outline">{counts[tab.key]}</Badge>
                            </button>
                        );
                    })}
                </div>
                <InputGroup className="w-64 rounded-full">
                    <InputGroupInput
                        type="search"
                        placeholder="Search tickers..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <InputGroupAddon align="inline-end">
                        <LuSearch />
                    </InputGroupAddon>
                </InputGroup>
            </div>

            {isTlhHoldingsLoading ? (
                <div className="text-cocoa-500 text-sm">Loading holdings…</div>
            ) : visibleHoldings.length === 0 ? (
                <div className="text-cocoa-500 text-sm italic">No holdings match the current filter.</div>
            ) : (
                <table className="w-full table-fixed text-sm">
                    <thead>
                        <tr className="text-cocoa-500 border-cocoa-200 border-b text-xs tracking-wider uppercase">
                            <th className="w-24 py-2 text-left font-medium">Ticker</th>
                            <th className="w-[32rem] py-2 text-left font-medium">Name</th>
                            <th className="w-32 py-2 text-left font-medium">Value</th>
                            <th className="py-2 text-left font-medium">Replacement</th>
                            <th className="w-8" />
                        </tr>
                    </thead>
                    <tbody className="divide-cocoa-200 divide-y">
                        {visibleHoldings.map((holding) => (
                            <HoldingRow
                                key={holding.sId}
                                holding={holding}
                                replacements={getEffectiveReplacements(holding)}
                                untouched={isUntouched(holding)}
                                onAddReplacement={(r) => addReplacement(holding, r)}
                                onRemoveReplacement={(sId) => removeReplacement(holding, sId)}
                                onClearReplacements={() => clearReplacements(holding.sId)}
                            />
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

interface HoldingRowProps {
    holding: TLHHolding;
    replacements: TLHReplacement[];
    untouched: boolean;
    onAddReplacement: (replacement: TLHReplacement) => void;
    onRemoveReplacement: (sId: string) => void;
    onClearReplacements: () => void;
}

const HoldingRow = ({
    holding,
    replacements,
    untouched,
    onAddReplacement,
    onRemoveReplacement,
    onClearReplacements,
}: HoldingRowProps) => (
    <tr className="group/holding">
        <td className="py-3 align-middle">
            <span className="text-cocoa-1000 font-medium">{holding.ticker}</span>
        </td>
        <td className="text-cocoa-700 truncate py-3 align-middle" title={holding.name}>
            {holding.name}
        </td>
        <td className="text-cocoa-1000 py-3 align-middle tabular-nums">{formatCurrency(holding.value)}</td>
        <td className="min-w-0 py-3 align-middle">
            <ReplacementsCell
                holding={holding}
                replacements={replacements}
                untouched={untouched}
                onAddReplacement={onAddReplacement}
                onRemoveReplacement={onRemoveReplacement}
            />
        </td>
        <td className="py-3 align-middle">
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        onClick={onClearReplacements}
                        aria-label={`Clear replacements for ${holding.ticker}`}
                        className="text-cocoa-400 hover:bg-cocoa-100 hover:text-cocoa-1000 flex h-7 w-7 items-center justify-center rounded opacity-0 transition group-hover/holding:opacity-100"
                    >
                        <LuX className="h-4 w-4" />
                    </button>
                </TooltipTrigger>
                <TooltipContent>Clear replacements</TooltipContent>
            </Tooltip>
        </td>
    </tr>
);

const ReplacementsCell = ({
    holding,
    replacements,
    untouched,
    onAddReplacement,
    onRemoveReplacement,
}: {
    holding: TLHHolding;
    replacements: TLHReplacement[];
    untouched: boolean;
    onAddReplacement: (replacement: TLHReplacement) => void;
    onRemoveReplacement: (sId: string) => void;
}) => {
    const addButton = (
        <AddReplacementButton holding={holding} existingReplacements={replacements} onAdd={onAddReplacement} />
    );
    if (untouched) {
        return (
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                <div className="flex max-w-full flex-wrap items-center gap-1.5 rounded-2xl border border-emerald-200 bg-emerald-100 py-1 pr-2 pl-3">
                    <LuSparkle className="h-3.5 w-3.5 text-emerald-500" />
                    {replacements.map((r) => (
                        <ReplacementChip
                            key={r.sId}
                            replacement={r}
                            onRemove={() => onRemoveReplacement(r.sId)}
                            className="border-cocoa-200 text-cocoa-1000 border bg-white"
                        />
                    ))}
                </div>
                {addButton}
            </div>
        );
    }
    return (
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            {replacements.map((r) => (
                <ReplacementChip
                    key={r.sId}
                    replacement={r}
                    onRemove={() => onRemoveReplacement(r.sId)}
                    className="bg-cocoa-100 text-cocoa-1000"
                />
            ))}
            {addButton}
        </div>
    );
};

const AddReplacementButton = ({
    holding,
    existingReplacements,
    onAdd,
}: {
    holding: TLHHolding;
    existingReplacements: TLHReplacement[];
    onAdd: (replacement: TLHReplacement) => void;
}) => {
    const [open, setOpen] = useState(false);
    const handlePick = (asset: AssetInfo) => {
        if (!asset.sId || !asset.symbol || !asset.scId) return;
        onAdd({
            sId: asset.sId,
            scId: asset.scId,
            ticker: asset.symbol,
            name: asset.description ?? asset.symbol,
        });
        setOpen(false);
    };
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            aria-label={`Add replacement for ${holding.ticker}`}
                            className="border-cocoa-300 text-cocoa-500 hover:border-cocoa-500 hover:text-cocoa-1000 flex h-7 w-7 items-center justify-center rounded-full border border-dashed transition"
                        >
                            <LuPlus className="h-3.5 w-3.5" />
                        </button>
                    </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>Add replacement</TooltipContent>
            </Tooltip>
            <PopoverContent className="w-80 p-3" align="start">
                <TickerSelectorInput
                    label="Add replacement"
                    placeholder="Search by ticker or company name..."
                    autoFocus
                    usePortal
                    excludeItems={[
                        {
                            sId: holding.sId,
                            scId: holding.scId,
                            displayText: holding.ticker,
                        },
                        ...existingReplacements.map((r) => ({
                            sId: r.sId,
                            scId: r.scId,
                            displayText: r.ticker,
                        })),
                    ]}
                    onSelectTicker={handlePick}
                />
            </PopoverContent>
        </Popover>
    );
};

const ChipRemoveButton = ({ ticker, onRemove }: { ticker: string; onRemove: () => void }) => (
    <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${ticker}`}
        className="bg-cocoa-900 hover:bg-cocoa-1000 absolute -top-1.5 -right-1.5 hidden h-4 w-4 items-center justify-center rounded-full leading-none text-white opacity-0 transition-opacity group-hover/chip:flex group-hover/chip:opacity-100"
    >
        <LuX className="h-3 w-3" strokeWidth={3} />
    </button>
);

const ReplacementChip = ({
    replacement,
    onRemove,
    className,
}: {
    replacement: TLHReplacement;
    onRemove: () => void;
    className: string;
}) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <span
                className={`group/chip relative inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${className}`}
            >
                {replacement.ticker}
                <ChipRemoveButton ticker={replacement.ticker} onRemove={onRemove} />
            </span>
        </TooltipTrigger>
        <TooltipContent side="bottom">{replacement.name}</TooltipContent>
    </Tooltip>
);
